import { Component } from 'react';

/* ErrorBoundary — wraps the entire app so an exception in any single
   component renders a friendly recovery card instead of a blank screen.
   The recovery card surfaces:
     • plain-language "something went wrong"
     • a "Try again" button (resets boundary state)
     • a "Force refresh" button (skips service-worker cache)
     • the error message in a collapsible dev panel for debugging
   This is critical for production stability — a single render error in a
   deep tree no longer takes the whole landing page offline. */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  forceRefresh = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (_) { /* best-effort cache purge */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        data-testid="error-boundary"
        className="fixed inset-0 flex items-center justify-center p-6"
        style={{ backgroundColor: '#0E1F2C', fontFamily: 'Manrope' }}
      >
        <div className="max-w-md w-full rounded-2xl p-6" style={{ backgroundColor: 'white' }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: '#FEE2E2' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
          </div>
          <h2 className="text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
            Something went wrong on this page
          </h2>
          <p className="text-sm mb-4" style={{ color: '#4A5D5D' }}>
            The app caught an unexpected error before the screen could render. Your data is safe — try again, or force-refresh to flush the cache.
          </p>
          <div className="flex gap-2 mb-4">
            <button
              data-testid="error-boundary-retry"
              onClick={this.reset}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: '#003DA5' }}
            >
              Try again
            </button>
            <button
              data-testid="error-boundary-refresh"
              onClick={this.forceRefresh}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-bold"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            >
              Force refresh
            </button>
          </div>
          <details className="text-[10px]" style={{ color: '#5C6B6B' }}>
            <summary className="cursor-pointer">Technical details</summary>
            <pre className="mt-2 p-2 rounded overflow-auto max-h-40" style={{ backgroundColor: '#F1F5F9', color: '#0F172A' }}>
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
